import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Progress } from '../ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { FolderUp, FileSpreadsheet, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getDocIcon = (filename) => {
  const ext = filename.split('.').pop()?.toLowerCase();
  if (['csv', 'txt'].includes(ext)) return FileSpreadsheet;
  return FileText;
};

export const ImportDocsDialog = ({ open, onClose, onImportComplete }) => {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [extractionStatus, setExtractionStatus] = useState([]);
  const [progress, setProgress] = useState(0);
  const [isExtracting, setIsExtracting] = useState(false);

  const loadDocs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/documents/extractable`, { headers: getAuthHeaders(), withCredentials: true });
      setDocuments(res.data);
    } catch {
      toast.error(t('toast.failed_to_load_documents'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (isOpen) => {
    if (isOpen) { setSelected([]); setExtractionStatus([]); setProgress(0); loadDocs(); }
    else onClose();
  };

  const toggle = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleAll = () => setSelected(selected.length === documents.length ? [] : documents.map(d => d.document_id));

  const handleExtract = async () => {
    if (selected.length === 0) { toast.error(t('toast.please_select_at_least_one_document')); return; }
    setIsExtracting(true);
    setProgress(0);
    setExtractionStatus(selected.map(id => ({ id, status: 'pending', count: 0 })));
    let total = 0;
    let years = new Set();
    let done = 0;
    for (const docId of selected) {
      setExtractionStatus(prev => prev.map(s => s.id === docId ? { ...s, status: 'extracting' } : s));
      try {
        const res = await axios.post(`${API}/documents/${docId}/extract`, null, { headers: getAuthHeaders(), withCredentials: true });
        total += res.data.count;
        res.data.years_found?.forEach(y => years.add(y));
        setExtractionStatus(prev => prev.map(s => s.id === docId ? { ...s, status: 'complete', count: res.data.count } : s));
      } catch (e) {
        setExtractionStatus(prev => prev.map(s => s.id === docId ? { ...s, status: 'error' } : s));
      }
      done++;
      setProgress(Math.round((done / selected.length) * 100));
    }
    setIsExtracting(false);
    if (total > 0) {
      toast.success(t('toast.extracted_count', { count: total }));
      if (years.size > 0) toast.info(`Data from years: ${Array.from(years).sort().reverse().join(', ')}`);
      onImportComplete?.();
    } else {
      toast.warning(t('toast.no_transactions_were_extracted_from_the_selected_documents'));
    }
    setTimeout(() => { onClose(); setSelected([]); setExtractionStatus([]); }, 2000);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderUp className="w-5 h-5 text-emerald-600" />
            Import from Documents
          </DialogTitle>
          <DialogDescription>Extract transactions from your uploaded documents</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-gray-500">Loading documents...</div>
        ) : documents.length === 0 ? (
          <div className="py-8 text-center">
            <FileText className="w-12 h-12 mx-auto text-gray-300 mb-2" />
            <p className="text-gray-500">No extractable documents found</p>
            <p className="text-sm text-gray-400 mt-1">Upload documents in the Documents section first</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {documents.map(doc => {
                const DocIcon = getDocIcon(doc.filename);
                const isSelected = selected.includes(doc.document_id);
                const status = extractionStatus.find(s => s.id === doc.document_id);
                const hasHistory = doc.extraction_status === 'success';
                return (
                  <div
                    key={doc.document_id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors
                      ${isSelected ? 'bg-emerald-50 border-emerald-300' : 'bg-white border-gray-200 hover:bg-gray-50'}
                      ${status?.status === 'error' ? 'bg-red-50 border-red-300' : ''}
                      ${doc.extraction_status === 'success' ? 'border-l-4 border-l-green-400' : ''}
                      ${doc.extraction_status === 'failed' ? 'border-l-4 border-l-red-400' : ''}`}
                    onClick={() => !isExtracting && toggle(doc.document_id)}
                    data-testid={`doc-select-${doc.document_id}`}
                  >
                    <Checkbox checked={isSelected} disabled={isExtracting} className="pointer-events-none" />
                    <DocIcon className="w-5 h-5 text-primary flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{doc.filename}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{doc.category}</span>
                        <span>•</span>
                        <span>{new Date(doc.uploaded_at).toLocaleDateString()}</span>
                        {hasHistory && !status && (
                          <>
                            <span>•</span>
                            <span className="text-green-600">✓ Imported ({doc.extraction_count})</span>
                          </>
                        )}
                      </div>
                    </div>
                    {status && (
                      <div className="flex-shrink-0">
                        {status.status === 'pending' && <div className="w-4 h-4 rounded-full bg-gray-300" />}
                        {status.status === 'extracting' && <div className="w-4 h-4 rounded-full bg-blue-500 animate-pulse" />}
                        {status.status === 'complete' && (
                          <div className="flex items-center gap-1 text-green-600">
                            <CheckCircle className="w-4 h-4" />
                            <span className="text-xs">{status.count}</span>
                          </div>
                        )}
                        {status.status === 'error' && <AlertCircle className="w-4 h-4 text-red-500" />}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{selected.length} of {documents.length} selected</span>
              <Button variant="outline" size="sm" onClick={toggleAll} disabled={isExtracting} data-testid="select-all-docs-btn">
                {selected.length === documents.length ? 'Deselect All' : 'Select All'}
              </Button>
            </div>
            {isExtracting && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm"><span>Extracting...</span><span>{progress}%</span></div>
                <Progress value={progress} />
              </div>
            )}
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800">
              <strong>Supported:</strong> CSV/TXT bank statements · PDF invoices/receipts · JPG/PNG receipt photos (OCR)
            </div>
            <Button onClick={handleExtract} disabled={isExtracting || selected.length === 0} className="w-full" data-testid="extract-docs-btn">
              {isExtracting ? 'Extracting...' : `Extract from ${selected.length} Document(s)`}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
